import { useState, useEffect, useRef } from "react";
import Button from "./Button";
import Icon from "../AppIcon";
import { KnowtranLogo } from "../../constants/images";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");
  const headerRef = useRef<HTMLElement>(null);
  const scrollPositionRef = useRef(0);
  const isMobileMenuOpenRef = useRef(false);
  const isNavigatingRef = useRef(false);

  // Sync the ref with state
  useEffect(() => {
    isMobileMenuOpenRef.current = isMobileMenuOpen;
  }, [isMobileMenuOpen]);

  // Track scroll position and active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      scrollPositionRef.current = scrollY;
      setIsScrolled(scrollY > 10);

      // ONLY update active section if drawer is NOT open
      if (isMobileMenuOpenRef.current) return;

      // Track active section for navigation highlighting
      const sections = ["overview", "about-us", "features", "demo-booking"];
      const scrollPosition = scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open - SIMPLER APPROACH
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Just hide overflow, don't mess with position
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      // Restore overflow
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";

      // Reset the navigating flag
      if (isNavigatingRef.current) {
        isNavigatingRef.current = false;
      }
    }

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    // Mark that we're navigating
    isNavigatingRef.current = true;

    // Close the menu
    setIsMobileMenuOpen(false);

    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = headerRef.current?.offsetHeight || 80;
        const elementPosition = element.offsetTop - headerHeight - 8;

        window.scrollTo({
          top: Math.max(0, elementPosition),
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleDemoClick = () => {
    scrollToSection("demo-booking");
  };

  const handleLoginClick = () => {
    window.open("https://app.knowtran.com");
  };

  const navigationItems = [
    { id: "overview", label: "Platform Overview", icon: "Home" },
    { id: "about-us", label: "About Us", icon: "Users" },
    { id: "features", label: "Features", icon: "Grid3X3" },
  ];

  return (
    <>
      {/* Header */}
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isMobileMenuOpen || isScrolled
            ? "aurora-glass border-b border-white/20"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <button
              onClick={() => scrollToSection("overview")}
              className="flex items-center focus:outline-none rounded-lg"
              aria-label="Go to home"
            >
              <div className="flex items-center justify-center">
                <img
                  src={KnowtranLogo}
                  alt="KnowTran"
                  className="w-16 h-16 object-contain"
                />
                <div className="flex flex-col items-start">
                  <p className="text-base font-semibold">Knowtran</p>
                  <p className="text-xs font-light">
                    Bridge to better education
                  </p>
                </div>
              </div>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`font-jakarta font-600 text-base transition-all duration-250 ease-out hover:text-primary ${
                    activeSection === item.id
                      ? "text-primary"
                      : "text-foreground"
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={handleLoginClick}
                className="font-jakarta font-600 text-base transition-all duration-250 ease-out hover:text-primary text-foreground"
              >
                Log In
              </button>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center">
              <Button
                variant="default"
                size="default"
                onClick={handleDemoClick}
                className="font-jakarta font-600 aurora-glow hover:shadow-aurora-glow"
                iconName="Play"
                iconPosition="left"
                iconSize={18}
              >
                Book Demo
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 transition-colors duration-250"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Icon
                name={isMobileMenuOpen ? "X" : "Menu"}
                size={24}
                color="currentColor"
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/60 z-40 animate-fade-in"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer */}
          <div
            className="lg:hidden fixed inset-x-0 top-20 bottom-0 z-40 aurora-glass overflow-y-auto overscroll-contain animate-slide-down"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col min-h-full">
              {/* Navigation Links */}
              <nav className="flex-1 px-6 py-8 space-y-6">
                {navigationItems.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`flex items-center space-x-3 w-full text-left font-jakarta font-600 text-lg transition-all duration-250 ease-out hover:text-primary transform ${
                      activeSection === item.id
                        ? "text-primary"
                        : "text-foreground"
                    }`}
                    style={{
                      animation: `fadeInUp 0.3s ease-out ${index * 0.1}s both`,
                    }}
                  >
                    <Icon name={item.icon} size={20} color="currentColor" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>

              {/* Mobile CTA Buttons */}
              <div className="px-6 py-6 border-t border-white/20 space-y-4">
                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  onClick={handleLoginClick}
                  className="font-jakarta font-600"
                >
                  Log In
                </Button>
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  onClick={handleDemoClick}
                  className="font-jakarta font-600 aurora-glow"
                  iconName="Play"
                  iconPosition="left"
                  iconSize={18}
                >
                  Book Demo
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add custom animations to styles */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default Header;
